import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { core } from "@/lib/mh";
import { errText } from "@/lib/http";
import { ManaMark } from "@/components/mh/Brand";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const Opt = ({ o, active, onClick, multi }) => (
  <button type="button" onClick={onClick} data-testid={`audit-opt-${o.value}`}
    className={cn("w-full text-left px-5 h-14 rounded-xl border flex items-center gap-3 transition-[border-color,background-color,transform] duration-200 active:scale-[0.99]",
      active ? "border-[#D4FF3A] bg-[#D4FF3A]/[0.06]" : "border-white/10 hover:border-white/30 bg-white/[0.02]")}>
    <span className={cn("w-5 h-5 grid place-items-center border flex-shrink-0", multi ? "rounded-md" : "rounded-full", active ? "bg-[#D4FF3A] border-[#D4FF3A] text-black" : "border-white/25")}>{active && <Check className="w-3 h-3" />}</span>
    <span className="text-[15px]">{o.label}</span>
  </button>
);

export const PublicTop = () => (
  <div className="flex items-center justify-between px-5 sm:px-10 h-16 border-b border-white/[0.07]">
    <Link to="/" className="flex items-center gap-2.5"><ManaMark /><span className="font-display text-xl">Manahouse</span></Link>
    <Link to="/login" className="font-mono text-xs text-muted-foreground hover:text-foreground" data-testid="public-signin-link">Sign in →</Link>
  </div>
);

export default function Audit() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [qs, setQs] = useState([]);
  const [step, setStep] = useState(-1);
  const [company, setCompany] = useState("");
  const [answers, setAnswers] = useState({ stack: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { core.auditQuestions().then((r) => setQs(r.data)).catch(() => {}); }, []);

  const q = qs[step];
  const choose = (v) => {
    if (q.type === "multi") {
      setAnswers((a) => ({ ...a, [q.id]: (a[q.id] || []).includes(v) ? a[q.id].filter((x) => x !== v) : [...(a[q.id] || []), v] }));
    } else {
      setAnswers((a) => ({ ...a, [q.id]: v }));
      setTimeout(() => setStep((s) => Math.min(s + 1, qs.length - 1)), 220);
    }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await core.submitAudit({ answers, company });
      nav(`/report/${data.id}`);
    } catch (e) {
      toast.error(errText(e));
      setBusy(false);
    }
  };
  const answered = q && (q.type === "multi" || answers[q.id]);
  const last = step === qs.length - 1;

  return (
    <div className="min-h-screen bg-[#030305] flex flex-col" data-testid="audit-page">
      {!user && <PublicTop />}
      <div className="h-[2px] bg-white/[0.06]"><div className="h-full bg-[#D4FF3A] transition-[width] duration-500" style={{ width: `${qs.length ? ((step + 1) / qs.length) * 100 : 0}%` }} /></div>
      <main className="flex-1 w-full max-w-2xl mx-auto px-5 sm:px-8 py-12 sm:py-20">
        {step === -1 ? (
          <div className="mh-rise" data-testid="audit-intro">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Free rollout audit · 8 questions · 5 minutes</div>
            <h1 className="font-display text-5xl sm:text-6xl mt-4 leading-[1.02]">Which 10 agents should you <em className="text-muted-foreground">build first?</em></h1>
            <p className="text-base text-muted-foreground mt-5">Get a readiness score, your top 10 agents with reasons, a 90-day plan in three phases and a shareable report. No signup.</p>
            <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name (optional)" data-testid="audit-company"
              className="mt-8 w-full h-12 px-4 rounded-xl bg-white/[0.03] border border-white/10 outline-none focus:border-[#D4FF3A]/60" />
            <button onClick={() => setStep(0)} disabled={!qs.length} data-testid="audit-start"
              className="mt-4 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#D4FF3A] text-black font-semibold hover:bg-[#e2ff73] transition-colors disabled:opacity-50">Start the audit <ArrowRight className="w-4 h-4" /></button>
          </div>
        ) : q && (
          <div key={q.id} className="mh-rise" data-testid={`audit-q-${q.id}`}>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">Question {step + 1} of {qs.length}</div>
            <h2 className="font-display text-4xl sm:text-5xl mt-3 mb-8">{q.prompt}</h2>
            <div className="space-y-2">{q.options.map((o) => <Opt key={o.value} o={o} multi={q.type === "multi"} active={q.type === "multi" ? (answers[q.id] || []).includes(o.value) : answers[q.id] === o.value} onClick={() => choose(o.value)} />)}</div>
            <div className="flex items-center justify-between mt-10">
              <button onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="audit-back"><ArrowLeft className="w-4 h-4" /> Back</button>
              {last ? (
                <button onClick={submit} disabled={!answered || busy} data-testid="audit-submit"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#D4FF3A] text-black font-semibold disabled:opacity-40">{busy ? "Building your plan…" : "See my plan"}</button>
              ) : (
                <button onClick={() => setStep((s) => s + 1)} disabled={!answered} data-testid="audit-next"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-full border border-white/15 hover:bg-white/5 disabled:opacity-40">Next <ArrowRight className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
